import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Video, Headphones, ClipboardList, ChevronRight, Clock, CheckCircle2, Layers } from 'lucide-react';
import { useUserProgram } from '@/hooks/useUserProgram';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';
import { useProduct, getProductNamespace } from '@/hooks/useProduct';
import { useProductDashboard } from '@/hooks/useProductDashboard';

const activityIcons: Record<string, React.ElementType> = {
  article: BookOpen,
  video: Video,
  audio: Headphones,
  assessment: ClipboardList,
};

const activityGradients: Record<string, string> = {
  article: 'from-primary/20 to-primary/30',
  video: 'from-blue-500/20 to-indigo-600/30',
  audio: 'from-purple-500/20 to-violet-600/30',
  assessment: 'from-orange-500/20 to-amber-600/30',
};

export function NextActivityCard() {
  const navigate = useNavigate();
  const { program, currentPhase, nextActivity, loading, completions, allPrograms } = useUserProgram();
  const { t } = useTranslation();
  const { productId } = useProduct();
  const { programPath, dashboardPath } = useProductDashboard();
  const productNs = getProductNamespace(productId);
  
  // Helper for product-specific translations
  const pt = (key: string, fallback: string) => t(`${productNs}:${key}`, fallback);

  const activityLabels: Record<string, string> = {
    article: pt('program.activityTypes.article', 'Article'),
    video: pt('program.activityTypes.video', 'Video'),
    audio: pt('program.activityTypes.audio', 'Audio'),
    assessment: pt('program.activityTypes.assessment', 'Assessment'),
  };

  if (loading) {
    return (
      <Card className="h-full">
        <CardContent className="p-6">
          <Skeleton className="h-6 w-32 mb-4" />
          <Skeleton className="h-32 w-full mb-4" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  // No programs available in the current language - hide the card
  if (allPrograms.length === 0) {
    return null;
  }

  // Programs exist but user hasn't selected one yet
  if (!program) {
    return (
      <Card className="h-full bg-gradient-to-br from-muted/50 to-muted border-muted">
        <CardContent className="p-6 flex flex-col items-center justify-center h-full text-center">
          <Layers className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="font-semibold text-lg mb-2">
            {pt('program.selectProgramTitle', 'Select a Program')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {pt('program.selectProgramDescription', "Choose a program from the available options below to get started.")}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!nextActivity) {
    return (
      <Card className="h-full bg-gradient-to-br from-primary/10 to-primary/20 border-primary/20">
        <CardContent className="p-6 flex flex-col items-center justify-center h-full text-center">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-8 h-8 text-primary" />
          </div>
          <h3 className="font-semibold text-xl text-primary mb-2">
            🎉 {pt('program.complete', 'Program Complete!')}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {pt('program.completeDescription', "Congratulations! You've completed all activities.")}
          </p>
          <Button onClick={() => navigate(programPath)} variant="outline" className="border-primary/30">
            {pt('program.viewFullProgress', 'View Full Progress')}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  const Icon = activityIcons[nextActivity.activity_type] || BookOpen;
  const typeLabel = activityLabels[nextActivity.activity_type] || pt('program.activity', 'Activity');
  const gradient = activityGradients[nextActivity.activity_type] || 'from-primary/20 to-secondary/30';
  const isCompleted = completions.some(c => c.phase_activity_id === nextActivity.id && c.status === 'completed');

  const handleStartActivity = () => {
    // Pass both the activity_id and phase_activity_id for completion tracking
    navigate(`${dashboardPath}?${nextActivity.activity_type}=${nextActivity.activity_id}&phaseActivityId=${nextActivity.id}`);
  };

  const getActionButtonText = () => {
    if (isCompleted) return pt('program.actions.reviewActivity', 'Review Activity');
    switch (nextActivity.activity_type) {
      case 'article':
        return pt('program.actions.readArticle', 'Read Article');
      case 'video':
        return pt('program.actions.watchVideo', 'Watch Video');
      case 'audio':
        return pt('program.actions.listenNow', 'Listen Now');
      case 'assessment':
        return pt('program.actions.takeAssessment', 'Take Assessment');
      default:
        return pt('program.actions.startActivity', 'Start Activity');
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return null;
    const mins = Math.round(seconds / 60);
    return `${mins} ${pt('program.minutes', 'min')}`;
  };

  return (
    <Card className={`h-full overflow-hidden bg-gradient-to-br ${gradient} border-primary/20`}>
      <CardContent className="p-6 flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
            {pt('program.yourNextActivity', 'Next Activity')}
          </h3>
          {currentPhase && (
            <Badge variant="outline" className="text-xs">
              {currentPhase.name}
            </Badge>
          )}
        </div>

        {/* Activity Preview */}
        <div className="flex-1 flex flex-col">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-4 rounded-xl bg-background/80 backdrop-blur-sm shadow-sm">
              <Icon className="w-8 h-8 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge variant="secondary" className="text-xs">
                  {typeLabel}
                </Badge>
                {isCompleted && (
                  <Badge className="text-xs bg-green-500/20 text-green-600 border-green-500/30">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {pt('program.completed', 'Completed')}
                  </Badge>
                )}
                {nextActivity.is_required && !isCompleted && (
                  <Badge variant="outline" className="text-xs">
                    {pt('program.required', 'Required')}
                  </Badge>
                )}
              </div>
              <h4 className="font-bold text-lg leading-tight mb-1">
                {nextActivity.content_title}
              </h4>
              {nextActivity.duration && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  {formatDuration(nextActivity.duration)}
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {nextActivity.content_description && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-4 flex-1">
              {nextActivity.content_description}
            </p>
          )}

          {/* CTA Button */}
          <Button 
            onClick={handleStartActivity} 
            className="w-full mt-auto"
            size="lg"
          >
            {getActionButtonText()}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
